import { addCalendarDays, localDateOf } from './clinic-day';
import { IPD_ORDER_PRIORITY, IPD_OVERDUE_ESCALATION_DEFAULT_MINUTES, IPD_OVERDUE_ESCALATION_MAX_MINUTES, IPD_STEPPED_FREQUENCY_MINUTES, IPD_TASK_PENDING, IPD_TIMED_FREQUENCIES, IPD_TRANSFUSION_DEFAULT_MINUTES, IPD_TRANSFUSION_STEPS, } from './dto/ipd-orders';
export const IPD_SCHEDULE_HORIZON_HOURS = 48;
export const IPD_SCHEDULE_TOP_UP_HOURS = 24;
export const IPD_SCHEDULE_BACKFILL_HOURS = 24;
export const IPD_PLAN_MAX_TASKS = 500;
export const DEFAULT_IPD_DOSE_TIMES = Object.freeze({
    od: ['08:00'],
    bd: ['08:00', '20:00'],
    tds: ['06:00', '14:00', '22:00'],
    qds: ['00:00', '06:00', '12:00', '18:00'],
    hs: ['21:00'],
    q4h: ['02:00', '06:00', '10:00', '14:00', '18:00', '22:00'],
    q6h: ['00:00', '06:00', '12:00', '18:00'],
    q8h: ['06:00', '14:00', '22:00'],
    q12h: ['08:00', '20:00'],
    q24h: ['08:00'],
});
export const DEFAULT_IPD_MEAL_TIMES = Object.freeze(['07:30', '12:30', '19:00']);
export const IPD_MEAL_LABELS = ['breakfast', 'lunch', 'dinner'];
export const DEFAULT_IPD_VITALS_INTERVAL_MINUTES = 240;
export const IPD_VITALS_INTERVAL_MAX_MINUTES = 1440;
export const IPD_PHYSIO_DAY = Object.freeze({ startMinute: 9 * 60, endMinute: 17 * 60 });
export const IPD_CONSULT_DUE_MINUTES = Object.freeze({ emergency: 30, urgent: 240, routine: 1440 });
export const IPD_TRANSFUSION_PREP_MINUTES = 15;
export const IPD_TRANSFUSION_FIRST_CHECK_MINUTES = 15;
export const IPD_TRANSFUSION_CHECK_MINUTES = 60;
export const IPD_TRANSFUSION_POST_MINUTES = 60;
export const IPD_OXYGEN_INITIAL_CHECK_MINUTES = 15;
export const IPD_SCHEDULE_DEFAULTS = Object.freeze({
    doseTimes: DEFAULT_IPD_DOSE_TIMES,
    mealTimes: DEFAULT_IPD_MEAL_TIMES,
    vitalsIntervalMinutes: DEFAULT_IPD_VITALS_INTERVAL_MINUTES,
    wardVitalsIntervalMinutes: Object.freeze({}),
});
const TIME_OF_DAY = /^([01]\d|2[0-3]):[0-5]\d$/;
function minuteOf(time) {
    const [hour, minute] = time.split(':').map(Number);
    return (hour ?? 0) * 60 + (minute ?? 0);
}
function timeOf(minute) {
    const wrapped = ((minute % 1440) + 1440) % 1440;
    return `${String(Math.floor(wrapped / 60)).padStart(2, '0')}:${String(wrapped % 60).padStart(2, '0')}`;
}
function timeList(value, max) {
    if (!Array.isArray(value))
        return undefined;
    const valid = value.filter((entry) => typeof entry === 'string' && TIME_OF_DAY.test(entry));
    if (valid.length === 0 || valid.length !== value.length || valid.length > max)
        return undefined;
    return [...new Set(valid)].sort((a, b) => minuteOf(a) - minuteOf(b));
}
function intervalMinutes(value) {
    return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= IPD_VITALS_INTERVAL_MAX_MINUTES && (value === 0 || value >= 15) ? value : undefined;
}
export function resolveIpdScheduleSettings(raw) {
    const source = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
    const doseSource = source.ipdDoseTimes && typeof source.ipdDoseTimes === 'object' ? source.ipdDoseTimes : {};
    const doseTimes = Object.fromEntries(IPD_TIMED_FREQUENCIES.map((code) => [code, timeList(doseSource[code], 24) ?? DEFAULT_IPD_DOSE_TIMES[code]]));
    const wardSource = source.ipdWardVitalsIntervalMinutes && typeof source.ipdWardVitalsIntervalMinutes === 'object' ? source.ipdWardVitalsIntervalMinutes : {};
    const wardVitalsIntervalMinutes = {};
    for (const [wardId, minutes] of Object.entries(wardSource)) {
        const valid = intervalMinutes(minutes);
        if (valid !== undefined)
            wardVitalsIntervalMinutes[wardId] = valid;
    }
    return {
        doseTimes,
        mealTimes: timeList(source.ipdMealTimes, 6) ?? DEFAULT_IPD_MEAL_TIMES,
        vitalsIntervalMinutes: intervalMinutes(source.ipdVitalsIntervalMinutes) ?? DEFAULT_IPD_VITALS_INTERVAL_MINUTES,
        wardVitalsIntervalMinutes,
    };
}
export function resolveIpdOverdueEscalationMinutes(raw) {
    const source = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
    const value = source.ipdOverdueEscalationMinutes;
    const valid = typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= IPD_OVERDUE_ESCALATION_MAX_MINUTES && (value === 0 || value >= 15);
    return valid ? value : IPD_OVERDUE_ESCALATION_DEFAULT_MINUTES;
}
export function ipdVitalsIntervalFor(settings, wardId) {
    const ward = wardId ? settings.wardVitalsIntervalMinutes[wardId] : undefined;
    return ward ?? settings.vitalsIntervalMinutes;
}
const formatters = new Map();
function formatter(timeZone) {
    let cached = formatters.get(timeZone);
    if (!cached) {
        cached = new Intl.DateTimeFormat('en-US', {
            timeZone,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
        formatters.set(timeZone, cached);
    }
    return cached;
}
function offsetMinutes(instant, timeZone) {
    const parts = formatter(timeZone).formatToParts(instant);
    const value = (type) => Number(parts.find((part) => part.type === type)?.value ?? '0');
    const asUtc = Date.UTC(value('year'), value('month') - 1, value('day'), value('hour'), value('minute'), value('second'));
    return (asUtc - Math.floor(instant.getTime() / 1000) * 1000) / 60_000;
}
function wallClockToUtc(date, minuteOfDay, timeZone) {
    const [year, month, day] = date.split('-').map(Number);
    const naive = Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1) + minuteOfDay * 60_000;
    const first = offsetMinutes(new Date(naive), timeZone);
    const candidate = new Date(naive - first * 60_000);
    const second = offsetMinutes(candidate, timeZone);
    return second === first ? candidate : new Date(naive - second * 60_000);
}
const MINUTE = 60_000;
const MAX_DAYS = 1500;
function task(taskKind, at, label = '') {
    return { taskKind, scheduledAt: at instanceof Date ? at : new Date(at), label };
}
function once(taskKind, at, label = '') {
    return [task(taskKind, at, label)];
}
function* daily(taskKind, times, notBefore, context, labelOf = () => '') {
    const minutes = [...new Set(times.filter((time) => TIME_OF_DAY.test(time)).map(minuteOf))].sort((a, b) => a - b);
    if (minutes.length === 0)
        return;
    const floor = notBefore.getTime();
    const startAt = Math.max(floor, context.from.getTime());
    let date = addCalendarDays(localDateOf(new Date(startAt), context.timeZone), -1);
    for (let day = 0; day < MAX_DAYS; day += 1, date = addCalendarDays(date, 1)) {
        for (const [index, minute] of minutes.entries()) {
            const at = wallClockToUtc(date, minute, context.timeZone);
            if (at.getTime() >= floor)
                yield task(taskKind, at, labelOf(index));
        }
    }
}
function* stepped(taskKind, stepMinutes, anchor, hint, label = '') {
    const step = stepMinutes * MINUTE;
    let index = Math.max(0, Math.ceil((hint.getTime() - anchor.getTime()) / step));
    for (;;) {
        yield task(taskKind, anchor.getTime() + index * step, label);
        index += 1;
    }
}
function* without(series, skip) {
    for (const item of series)
        if (!skip(item))
            yield item;
}
export function ipdGridTimes(minutes) {
    if (!Number.isInteger(minutes) || minutes <= 0 || 1440 % minutes !== 0)
        return null;
    return Array.from({ length: 1440 / minutes }, (_, index) => timeOf(index * minutes));
}
function interval(taskKind, minutes, start, context) {
    const grid = ipdGridTimes(minutes);
    return grid ? daily(taskKind, grid, start, context) : stepped(taskKind, minutes, start, context.from);
}
function* timedWithFirst(taskKind, times, startsAt, first, context) {
    if (first)
        yield task(taskKind, first);
    const nominal = 1440 / Math.max(1, times.length);
    const notBefore = first ? new Date(first.getTime() + (nominal / 2) * MINUTE) : startsAt;
    yield* daily(taskKind, times, notBefore, context);
}
function frequencySeries(taskKind, frequency, startsAt, first, context) {
    if (!frequency)
        return [];
    if (frequency === 'once' || frequency === 'stat')
        return once(taskKind, first ?? startsAt);
    const step = IPD_STEPPED_FREQUENCY_MINUTES[frequency];
    if (step)
        return stepped(taskKind, step, first ?? startsAt, context.from);
    const times = context.settings.doseTimes[frequency] ?? DEFAULT_IPD_DOSE_TIMES[frequency];
    return times ? timedWithFirst(taskKind, times, startsAt, first, context) : [];
}
function medication(order, details, context) {
    if (details.prn)
        return [];
    const first = details.firstDoseAt ? new Date(details.firstDoseAt) : order.priority === 'stat' ? order.startsAt : null;
    if (details.intervalMinutes)
        return stepped('dose', details.intervalMinutes, first ?? order.startsAt, context.from);
    return frequencySeries('dose', details.frequency, order.startsAt, first, context);
}
export function ipdIvDurationMinutes(details) {
    if (details.durationMinutes)
        return details.durationMinutes;
    const rate = details.rateMlPerHour && details.rateMlPerHour > 0 ? details.rateMlPerHour : details.volumeMl;
    return Math.max(5, Math.round((details.volumeMl / rate) * 60));
}
export function ipdIvBagLabel(bag) {
    return `bag-${bag}`;
}
function* ivFluid(order, details, context) {
    const duration = ipdIvDurationMinutes(details) * MINUTE;
    const check = details.checkIntervalMinutes * MINUTE;
    const first = details.firstBagAt ? Date.parse(details.firstBagAt) : order.startsAt.getTime();
    const skip = details.continuous ? Math.max(0, Math.floor((context.from.getTime() - first) / duration) - 1) : 0;
    for (let bag = skip; details.continuous || bag < details.bags; bag += 1) {
        const start = first + bag * duration;
        const end = start + duration;
        const label = ipdIvBagLabel(bag + 1);
        yield task('iv_start', start, label);
        for (let at = start + check; at < end; at += check)
            yield task('iv_check', at, label);
        yield task('iv_end', end, label);
    }
}
function diet(order, details, context) {
    if (details.dietType === 'npo')
        return [];
    const times = [...context.settings.mealTimes].sort((a, b) => minuteOf(a) - minuteOf(b));
    const meals = daily('meal', times, order.startsAt, context, (index) => IPD_MEAL_LABELS[index] ?? `meal-${index + 1}`);
    const npoFrom = details.npoFrom ? Date.parse(details.npoFrom) : null;
    const npoUntil = details.npoUntil ? Date.parse(details.npoUntil) : null;
    if (npoFrom === null && npoUntil === null)
        return meals;
    return without(meals, (meal) => {
        const at = meal.scheduledAt.getTime();
        return (npoFrom === null || at >= npoFrom) && (npoUntil === null || at < npoUntil);
    });
}
function nursing(order, details, context) {
    if (details.intervalMinutes)
        return interval('nursing', details.intervalMinutes, order.startsAt, context);
    return frequencySeries('nursing', details.frequency ?? 'once', order.startsAt, null, context);
}
export function ipdTransfusionMinutesPerUnit(details) {
    if (details.durationMinutesPerUnit)
        return details.durationMinutesPerUnit;
    if (details.rateMlPerHour && details.volumeMlPerUnit)
        return Math.max(10, Math.round((details.volumeMlPerUnit / details.rateMlPerHour) * 60));
    return IPD_TRANSFUSION_DEFAULT_MINUTES[details.product];
}
export function ipdTransfusionLabel(unit, step) {
    return `unit-${unit}:${step}`;
}
function* transfusion(order, details) {
    const perUnit = ipdTransfusionMinutesPerUnit(details) * MINUTE;
    let prepAt = details.firstUnitAt ? Date.parse(details.firstUnitAt) : order.startsAt.getTime();
    for (let unit = 1; unit <= details.units; unit += 1) {
        const at = (step, when) => task('transfusion_check', when, ipdTransfusionLabel(unit, step));
        const start = prepAt + IPD_TRANSFUSION_PREP_MINUTES * MINUTE;
        const end = start + perUnit;
        yield at('pre_vitals', prepAt);
        yield at('start', start);
        if (start + IPD_TRANSFUSION_FIRST_CHECK_MINUTES * MINUTE < end)
            yield at('check_15', start + IPD_TRANSFUSION_FIRST_CHECK_MINUTES * MINUTE);
        for (let when = start + IPD_TRANSFUSION_CHECK_MINUTES * MINUTE; when < end; when += IPD_TRANSFUSION_CHECK_MINUTES * MINUTE)
            yield at('check', when);
        yield at('end', end);
        if (unit === details.units)
            yield at('post_vitals', end + IPD_TRANSFUSION_POST_MINUTES * MINUTE);
        prepAt = end + IPD_TRANSFUSION_PREP_MINUTES * MINUTE;
    }
}
function* oxygen(order, details, context) {
    const initial = order.startsAt.getTime() + IPD_OXYGEN_INITIAL_CHECK_MINUTES * MINUTE;
    yield task('o2_check', initial, 'initial');
    const next = new Date(initial + (details.checkIntervalMinutes / 2) * MINUTE);
    const grid = ipdGridTimes(details.checkIntervalMinutes);
    yield* grid ? daily('o2_check', grid, next, context) : stepped('o2_check', details.checkIntervalMinutes, new Date(initial + details.checkIntervalMinutes * MINUTE), context.from);
}
export function ipdPhysioTimes(sessionsPerDay) {
    const sessions = Math.min(6, Math.max(1, Math.floor(sessionsPerDay)));
    const span = IPD_PHYSIO_DAY.endMinute - IPD_PHYSIO_DAY.startMinute;
    return Array.from({ length: sessions }, (_, index) => timeOf(IPD_PHYSIO_DAY.startMinute + Math.round(((index + 0.5) * span) / sessions / 5) * 5));
}
function legacy(order, context) {
    if (order.isStat)
        return once('dose', order.startsAt);
    const times = (order.scheduleTimes ?? []).filter((time) => TIME_OF_DAY.test(time));
    return times.length > 0 ? daily('dose', times, order.startsAt, context) : [];
}
function seriesOf(order, context) {
    const details = order.details;
    if (!details)
        return legacy(order, context);
    switch (details.kind) {
        case 'medication':
            return medication(order, details, context);
        case 'iv_fluid':
            return ivFluid(order, details, context);
        case 'diet':
            return diet(order, details, context);
        case 'nursing':
            return nursing(order, details, context);
        case 'procedure':
            return once('procedure_due', details.scheduledAt ? new Date(details.scheduledAt) : order.startsAt);
        case 'lab':
            return once('collect', details.collectAt ? new Date(details.collectAt) : order.startsAt);
        case 'radiology':
            return [];
        case 'blood_transfusion':
            return transfusion(order, details);
        case 'oxygen':
            return oxygen(order, details, context);
        case 'physiotherapy':
            return daily('physio', ipdPhysioTimes(details.sessionsPerDay), order.startsAt, context);
        case 'consult':
            return once('consult_due', new Date(order.startsAt.getTime() + IPD_CONSULT_DUE_MINUTES[details.urgency] * MINUTE));
        case 'vitals_monitoring':
            return interval('vitals_round', details.intervalMinutes, order.startsAt, context);
        default:
            return [];
    }
}
export function planOrderTasks(order, window) {
    const context = { from: window.from, timeZone: window.timeZone, settings: window.settings ?? IPD_SCHEDULE_DEFAULTS };
    const fromMs = window.from.getTime();
    const untilMs = window.until.getTime();
    const endMs = order.endsAt ? order.endsAt.getTime() : Number.POSITIVE_INFINITY;
    const tasks = [];
    for (const item of seriesOf(order, context)) {
        const at = item.scheduledAt.getTime();
        if (at >= endMs)
            return { tasks, through: null };
        if (at >= untilMs)
            return { tasks, through: window.until };
        if (at < fromMs)
            continue;
        tasks.push(item);
        if (tasks.length >= IPD_PLAN_MAX_TASKS)
            return { tasks, through: new Date(at) };
    }
    return { tasks, through: null };
}
export function ipdOrderEndsAt(startsAt, endsAt, details) {
    if (endsAt)
        return endsAt;
    const days = details && (details.kind === 'medication' || details.kind === 'physiotherapy') ? details.durationDays : undefined;
    return days ? new Date(startsAt.getTime() + days * 86_400_000) : null;
}
export function ipdOrderState(order) {
    if (order.status === 'discontinued')
        return 'stopped';
    if (order.status === 'completed')
        return 'completed';
    return order.heldAt ? 'held' : 'active';
}
export function ipdOrderPriority(order) {
    if (order.isStat)
        return 'stat';
    return IPD_ORDER_PRIORITY.includes(order.priority ?? '') ? order.priority : 'routine';
}
export function parseIpdTaskLabel(label) {
    const text = label ?? '';
    const bag = /^bag-(\d+)$/.exec(text);
    if (bag)
        return { bag: Number(bag[1]) };
    const unit = /^unit-(\d+):([a-z_0-9]+)$/.exec(text);
    if (unit && IPD_TRANSFUSION_STEPS.includes(unit[2] ?? ''))
        return { unit: Number(unit[1]), step: unit[2] };
    if (text === 'initial')
        return { initial: true };
    if (IPD_MEAL_LABELS.includes(text) || /^meal-\d+$/.test(text))
        return { meal: text };
    return {};
}
export const IPD_TASK_WINDOWS = Object.freeze({
    dose: { leadMinutes: 30, graceMinutes: 30, missedAfterMinutes: 120 },
    iv_start: { leadMinutes: 30, graceMinutes: 30, missedAfterMinutes: 120 },
    iv_check: { leadMinutes: 15, graceMinutes: 30, missedAfterMinutes: 120 },
    iv_end: { leadMinutes: 30, graceMinutes: 30, missedAfterMinutes: 120 },
    meal: { leadMinutes: 30, graceMinutes: 60, missedAfterMinutes: 180 },
    nursing: { leadMinutes: 30, graceMinutes: 60, missedAfterMinutes: 240 },
    vitals_round: { leadMinutes: 30, graceMinutes: 30, missedAfterMinutes: 120 },
    transfusion_check: { leadMinutes: 5, graceMinutes: 10, missedAfterMinutes: 30 },
    o2_check: { leadMinutes: 15, graceMinutes: 30, missedAfterMinutes: 120 },
    physio: { leadMinutes: 60, graceMinutes: 120, missedAfterMinutes: 480 },
    consult_due: { leadMinutes: 0, graceMinutes: 60, missedAfterMinutes: 1440 },
    procedure_due: { leadMinutes: 60, graceMinutes: 120, missedAfterMinutes: 1440 },
    collect: { leadMinutes: 15, graceMinutes: 30, missedAfterMinutes: 120 },
});
export const IPD_TASK_EARLY_LIMIT_MINUTES = 240;
export const IPD_DOSE_EARLY_REASON_MINUTES = 60;
export const IPD_TASK_FUTURE_SKEW_MINUTES = 5;
export function ipdTaskWindowRule(taskKind) {
    return IPD_TASK_WINDOWS[taskKind] ?? IPD_TASK_WINDOWS.dose;
}
function instantOf(value) {
    return value instanceof Date ? value.getTime() : Date.parse(value);
}
export function ipdTaskStatus(taskRow, now = new Date()) {
    if (taskRow.status !== IPD_TASK_PENDING)
        return taskRow.status;
    const rule = ipdTaskWindowRule(taskRow.taskKind);
    const lateBy = (now.getTime() - instantOf(taskRow.scheduledAt)) / MINUTE;
    if (lateBy < -rule.leadMinutes)
        return 'upcoming';
    if (lateBy <= rule.graceMinutes)
        return 'due';
    if (lateBy <= rule.missedAfterMinutes)
        return 'overdue';
    return 'missed';
}
export function ipdTaskMissedBefore(taskKind, now) {
    return new Date(now.getTime() - ipdTaskWindowRule(taskKind).missedAfterMinutes * MINUTE);
}
export function ipdTaskIsLate(taskKind, scheduledAt, at) {
    return at.getTime() - instantOf(scheduledAt) > ipdTaskWindowRule(taskKind).graceMinutes * MINUTE;
}
export function ipdRecordedStatus(outcome, taskKind, scheduledAt, at) {
    if (outcome !== 'given')
        return outcome;
    return ipdTaskIsLate(taskKind, scheduledAt, at) ? 'given_late' : 'given';
}
export const IPD_LONGEST_MISSED_AFTER_MINUTES = Math.max(...Object.values(IPD_TASK_WINDOWS).map((rule) => rule.missedAfterMinutes));
//# sourceMappingURL=ipd-schedule.js.map